"use client";

import { type FC, type ReactNode } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { agreement } from "@const/agreement";
import Translations from "@translate/components/SignInIntro/IntroPrimaryAuthCta";
import { IntroFreeCheckupChip } from "./IntroFreeCheckupChip";
import type { IntroAuthPlatform } from "./resolveIntroAuthPlatform";

const I18n = I18nUtil.createScoped(Translations);

export interface IntroPrimaryAuthCtaProps {
  platform: IntroAuthPlatform;
  slideIndex: number;
  variant?: "default" | "theme_pack" | "etf_overlap";
  /** Chip 第二段文案；缺省时 theme_pack 为 "ranking pack"，default 为 "money check-up" */
  chipLabel?: string;
  onApple?: () => void;
  onGoogle?: () => void;
  onGetStarted?: () => void;
  /** App：Other options → Sign in；PC 不渲染 */
  onOtherOptions?: () => void;
  termsSlot?: ReactNode;
  /**
   * 本次进入带着链接来的邀请码（fe-spec D26）→ Web 改用「Continue with Google +
   * Other options」布局（Figma `110013:130119` / PC `110013:130988`）；App 与主题包本就是这套布局。
   */
  inviteLinkEntry?: boolean;
  /** 邀请码入口行（含弹层），由 SignInIntroPhase 注入；新布局在 Other options 下方，旧布局在 Get Started 下方 */
  inviteEntrySlot?: ReactNode;
  className?: string;
}

const PRIMARY_BUTTON_CLASS = cn(
  "button flex items-center justify-center gap-1 rounded-lg shrink-0",
  "bg-background_solid_neutral_primary text-content_primary",
  "button_l h-12 w-full max-w-[361px] px-4 py-3",
  "md:h-14 md:w-auto md:min-w-[80px] md:px-10",
);

function openAgreementUrl(url: string) {
  if (typeof window === "undefined") {
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** 条款纯文案链（非 Checkbox）— audit #12 / fe-spec §4.6 */
export const IntroAuthTermsRow: FC<{
  className?: string;
  showCheckedBox?: boolean;
}> = ({ className, showCheckedBox = false }) => (
  <p
    className={cn(
      "body_s flex w-full items-start gap-2 pt-1 text-content_inverse_primary",
      className,
    )}
    data-testid="intro-auth-terms"
    data-node-id="102939:58231"
  >
    {showCheckedBox ? (
      <span
        aria-hidden="true"
        className="flex size-4 shrink-0 items-center justify-center rounded-lg bg-background_brand_overlay100 text-content_inverse_primary"
      >
        ✓
      </span>
    ) : null}
    <span>
      {I18n.t("By continuing, you agree to the")}{" "}
      <button
        type="button"
        className="underline decoration-solid"
        data-openreplay-label="intro_terms_of_use"
        onClick={() => openAgreementUrl(agreement.termsLink)}
      >
        {I18n.t("Terms of Use")}
      </button>{" "}
      {I18n.t("and")}{" "}
      <button
        type="button"
        className="underline decoration-solid"
        data-openreplay-label="intro_privacy_policy"
        onClick={() => openAgreementUrl(agreement.privacyLink)}
      >
        {I18n.t("Privacy Policy")}
      </button>
      .
    </span>
  </p>
);

/**
 * Intro 底栏主 CTA — iOS Apple / Android Google / PC Get Started
 * Figma: 102803:53571 / 53620 / PC 102839:40348
 */
export const IntroPrimaryAuthCta: FC<IntroPrimaryAuthCtaProps> = ({
  platform,
  slideIndex,
  variant = "default",
  chipLabel,
  onApple,
  onGoogle,
  onGetStarted,
  onOtherOptions,
  termsSlot,
  inviteLinkEntry = false,
  inviteEntrySlot,
  className,
}) => {
  const isApp = platform === "ios" || platform === "android";
  // 广告 Intro 变体（资料包 / ETF 重叠）共用同一份设计稿实例：App 版式 + 勾选条款 + 定位在左栏下部
  const isAdVariant = variant === "theme_pack" || variant === "etf_overlap";
  // D26：Web 带链接码进入 → 与 App / 广告 Intro 同一套 Google + Other options 布局
  const isInviteLayout = inviteLinkEntry && !isApp && !isAdVariant;
  const primaryLabel =
    platform === "ios"
      ? I18n.t("Continue with Apple")
      : platform === "android" || isAdVariant || isInviteLayout
        ? I18n.t("Continue with Google")
        : I18n.t("Get Started");

  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 z-20 w-full px-4",
        isAdVariant
          ? "pb-[calc(env(safe-area-inset-bottom,0px)+24px)] md:bottom-auto md:left-[72px] md:top-[min(75%,730px)] md:w-[328px] md:px-0 md:pb-0"
          : isInviteLayout
            ? "pb-6 pb-safe md:bottom-10 md:left-[72px] md:w-[460px] md:px-0 md:pb-0"
            : "pb-6 pb-safe md:bottom-10 md:left-[72px] md:w-auto md:max-w-none md:px-0 md:pb-0",
        className,
      )}
      data-testid="intro-primary-auth-cta"
      data-platform={platform}
      data-slide-index={slideIndex}
      data-invite-layout={isInviteLayout ? "true" : undefined}
    >
      {isApp || isAdVariant || isInviteLayout ? (
        <div
          className={cn(
            "mx-auto flex w-full max-w-[361px] flex-col items-center gap-2",
            isInviteLayout && "md:max-w-none",
          )}
        >
          <IntroFreeCheckupChip
            layout="app"
            label={
              chipLabel ??
              (variant === "theme_pack" ? I18n.t("ranking pack") : undefined)
            }
          />
          {platform === "ios" ? (
            <button
              type="button"
              data-testid="intro-primary-auth-button"
              data-openreplay-label="intro_continue_apple"
              onClick={onApple}
              className={cn(PRIMARY_BUTTON_CLASS, isAdVariant && "md:w-full")}
            >
              <Icon
                name="apple"
                size={24}
                className="shrink-0 text-content_primary"
              />
              <span>{primaryLabel}</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="intro-primary-auth-button"
              data-openreplay-label="intro_continue_google"
              onClick={onGoogle}
              className={cn(
                PRIMARY_BUTTON_CLASS,
                isAdVariant && "md:w-full",
                // PC 邀请布局：按钮撑满 460px 列（Figma 110013:131038）
                isInviteLayout && "md:w-full md:max-w-none",
              )}
            >
              <Icon name="Google with colour" size={24} className="shrink-0" />
              <span>{primaryLabel}</span>
            </button>
          )}
          <button
            type="button"
            data-testid="intro-other-options"
            data-openreplay-label="intro_other_options"
            onClick={onOtherOptions}
            className={cn(
              "button_m flex w-full items-center justify-center rounded-lg px-4",
              // Figma Secondary links（110013:130153 / 130208 / 131078）：有邀请码入口行时 Other options 高 44
              inviteEntrySlot ? "h-11" : "h-12",
              "text-content_inverse_primary",
            )}
          >
            {I18n.t("Other options")}
          </button>
          {/* Figma Secondary links：Other options（44）与入口行 gap 8 = 外层 gap-2；
              入口行无码态是 44 高按钮、已应用态是 20 高文字行。 */}
          {inviteEntrySlot && <div className="w-full">{inviteEntrySlot}</div>}
          {termsSlot ?? (
            <IntroAuthTermsRow showCheckedBox={isAdVariant || isInviteLayout} />
          )}
        </div>
      ) : (
        <div
          className={cn(
            "mx-auto flex w-full max-w-[361px] flex-col items-center gap-2 md:mx-0 md:w-auto md:max-w-none md:flex-row md:items-center md:gap-3",
            Boolean(inviteEntrySlot) && "md:flex-wrap",
          )}
          data-node-id="102839:40347"
          data-testid="intro-pc-cta-row"
        >
          {/* H5：chip 在按钮上方（稿 102803:53565）；md+：chip 在按钮右侧 */}
          <div className="md:hidden" data-testid="intro-free-checkup-chip-h5">
            <IntroFreeCheckupChip layout="app" />
          </div>
          <button
            type="button"
            data-testid="intro-get-started-cta"
            data-openreplay-label="intro_get_started"
            onClick={onGetStarted}
            className={PRIMARY_BUTTON_CLASS}
          >
            {primaryLabel}
          </button>
          <div
            className="hidden md:block"
            data-testid="intro-free-checkup-chip-pc"
          >
            <IntroFreeCheckupChip layout="pc" />
          </div>
          {/* 旧布局：入口行在 Get Started 正下方（D26）；md+ 横排时换行，与 CTA 左对齐 */}
          {inviteEntrySlot && (
            <div className="w-full md:basis-full">{inviteEntrySlot}</div>
          )}
        </div>
      )}
    </div>
  );
};
